import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EpisodeSummary, TvDetails } from '@/types/streaming';
import { SeasonRow, type SeasonStatus } from './SeasonRow';

interface TvStreamRef {
  type: 'tv';
  tmdbId: number;
  season: number;
  episode: number;
}

interface Props {
  streamRef: TvStreamRef;
  seasons: TvDetails['seasons'];
  episodes: Record<number, EpisodeSummary[]>;
  loadSeason: (season: number) => Promise<void>;
}

export function EpisodePickerDesktop({
  streamRef,
  seasons,
  episodes,
  loadSeason,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const sortedSeasons = useMemo(
    () => [...seasons].sort((a, b) => a.seasonNumber - b.seasonNumber),
    [seasons],
  );

  // Pick the initial expanded season: the user's current season if it exists,
  // otherwise the highest available season number. Null if there are none.
  const initialSeason = useMemo<number | null>(() => {
    if (sortedSeasons.length === 0) return null;
    if (sortedSeasons.some((s) => s.seasonNumber === streamRef.season)) {
      return streamRef.season;
    }
    return sortedSeasons[sortedSeasons.length - 1].seasonNumber;
  }, [sortedSeasons, streamRef.season]);

  const [openSeason, setOpenSeason] = useState<number | null>(initialSeason);
  const [statusMap, setStatusMap] = useState<Record<number, 'loading' | 'error'>>({});

  const triggerLoad = useCallback(
    async (season: number): Promise<void> => {
      setStatusMap((prev) => ({ ...prev, [season]: 'loading' }));
      try {
        await loadSeason(season);
        setStatusMap((prev) => {
          if (!(season in prev)) return prev;
          const next = { ...prev };
          delete next[season];
          return next;
        });
      } catch {
        setStatusMap((prev) => ({ ...prev, [season]: 'error' }));
      }
    },
    [loadSeason],
  );

  useEffect(() => {
    if (openSeason == null) return;
    if (episodes[openSeason]) return;
    const cur = statusMap[openSeason];
    if (cur === 'loading' || cur === 'error') return;
    void triggerLoad(openSeason);
  }, [openSeason, episodes, statusMap, triggerLoad]);

  function toggle(season: number): void {
    setOpenSeason((cur) => (cur === season ? null : season));
  }

  if (sortedSeasons.length === 0) {
    return (
      <div className="episode-picker" data-testid="episode-picker">
        <div className="episode-picker__empty">{t('episodes.emptyPicker')}</div>
      </div>
    );
  }

  return (
    <div className="episode-picker" data-testid="episode-picker">
      {sortedSeasons.map((s) => {
        const isOpen = openSeason === s.seasonNumber;
        const eps = episodes[s.seasonNumber] ?? null;
        let status: SeasonStatus;
        if (statusMap[s.seasonNumber] === 'loading') status = 'loading';
        else if (statusMap[s.seasonNumber] === 'error') status = 'error';
        else if (eps) status = 'ready';
        else status = 'idle';
        return (
          <SeasonRow
            key={s.seasonNumber}
            tmdbId={streamRef.tmdbId}
            seasonNumber={s.seasonNumber}
            name={s.name}
            episodeCount={s.episodeCount}
            isOpen={isOpen}
            status={status}
            episodes={isOpen ? eps : null}
            currentEpisode={s.seasonNumber === streamRef.season ? streamRef.episode : null}
            onToggle={() => toggle(s.seasonNumber)}
            onRetry={() => void triggerLoad(s.seasonNumber)}
          />
        );
      })}
    </div>
  );
}
